import React, { createRef, useEffect, useRef, useState } from 'react'
import FormBuilderAction from './FormBuilderAction'
import { Link, useNavigate } from 'react-router-dom'
import { KTSVG } from '../../../helpers'
import { useQuery } from '@apollo/client'
import {
  GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID,
  GET_USER_EMAIL_SIGNATURE
} from '../../../graphql/query'
import FormCardPreviewModal, { FormPreviewInfo } from './formBuilderModel/FormCardPreviewModal'
import UploadFormModal from './formBuilderModel/UploadFormModal'
import AddSignatureModal from './formBuilderModel/AddSignatureModal'
import CreateFormModal from './formBuilderModel/CreateFormModal'
import { getFormTypeBadgeClass, getFormTypeLabel } from './formBuilderModel/formTypeUtils'
import './FormBuilder.scss'

const FormBuilder = () => {
  const userActionDropdownMenuRefs = useRef([])
  const [isLoading, setIsLoading] = useState(false)
  const [isPreviewModalOpen, setPreviewIsModalOpen] = useState(false)
  const [previewFormInfo, setPreviewFormInfo] = useState<FormPreviewInfo | null>(null)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false)
  const [showTemplates, setShowTemplates] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const navigate = useNavigate()

  const { loading, error, data } = useQuery(GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID, {
    variables: {
      input: {
        formUuid: null
      }
    },
    context: {
      headers: {
        'access-control-allow-methods': 'Form-Builder'
      }
    }
  })

  const { data: signatureData, refetch: refetchSignature } = useQuery(GET_USER_EMAIL_SIGNATURE, {
    context: {
      headers: {
        'access-control-allow-methods': 'Form-Builder'
      }
    }
  })

  const hasEmailSignature = Boolean(signatureData?.getUserEmailSignature?.hasSignature)

  // Template data for the top section
  const templates = [
    {
      name: 'Feedback',
      headerColor: '#4A90E2', // A nice blue for the header
      imageUrl: 'https://botgo.s3.ap-south-1.amazonaws.com/92308951750764679330-rs3.png',
      description: 'Collect customer feedback and ratings'
    },
    {
      name: 'Registration',
      headerColor: '#50E3C2', // A teal/green color
      imageUrl: 'https://botgo.s3.ap-south-1.amazonaws.com/10620131750764639607-rs2.png',
      description: 'User registration and signup forms'
    },
    {
      name: 'Survey',
      headerColor: '#F5A623', // An orange/yellow color
      imageUrl: 'https://botgo.s3.ap-south-1.amazonaws.com/91471841750764721410-rs4.png',
      description: 'Research surveys and questionnaires'
    },
    {
      name: 'Research',
      headerColor: '#7ED321', // A light green color
      imageUrl: 'https://botgo.s3.ap-south-1.amazonaws.com/81876001750764556521-rs1.png',
      description: 'Academic and market research forms'
    }
  ]

  // Predefined gradient backgrounds for cards
  const gradientBackgrounds = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #ff8a80 0%, #ea4c89 100%)',
    'linear-gradient(135deg, #8fd3f4 0%, #84fab0 100%)'
  ]

  useEffect(() => {
    if (
      data?.getAllFormBuilderByBgAccountId?.data &&
      data?.getAllFormBuilderByBgAccountId?.data.length !== undefined
    ) {
      userActionDropdownMenuRefs.current = data.getAllFormBuilderByBgAccountId.data.map(
        (item: any, i: any) => userActionDropdownMenuRefs.current[i] ?? createRef()
      )
    }
  }, [data])

  if (loading) {
    return (
      <div
        className='d-flex justify-content-center align-items-center'
        style={{ minHeight: '400px' }}
      >
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (error) {
    console.log(error)
  }

  const formBuilderData = data?.getAllFormBuilderByBgAccountId?.data || []

  const openFormPreview = (formInfo: FormPreviewInfo) => {
    setPreviewFormInfo(formInfo)
    setPreviewIsModalOpen(true)
  }

  const closePreviewModal = () => {
    setPreviewIsModalOpen(false)
    setPreviewFormInfo(null)
  }

  const openUploadModal = () => {
    setIsUploadModalOpen(true)
  }

  const closeUploadModal = () => {
    setIsUploadModalOpen(false)
  }

  const openModal = () => {
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const getCardGradient = (index: number) => {
    return gradientBackgrounds[index % gradientBackgrounds.length]
  }

  const handleTemplateClick = (name: any) => {
    navigate('/automation/process-automation/form-builder/create-new-form')
    localStorage.setItem('formType', name)
  }

  return (
    <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', padding: '10px' }}>
      <div className='container-fluid p-0'>
        {/* Header Section with Action Buttons */}
        <div className='d-flex justify-content-between align-items-center mb-4'>
          <div>
            <div className='d-flex justify-content-between align-items-center w-100'>
              {/* <div>
              <h2 className='card-title fw-bold text-dark mb-2'>Form Builder</h2>
              <p className='text-muted mb-0'>Create and manage your forms with ease</p>
            </div> */}
              <div className='d-flex gap-3'>
                <button
                  onClick={openModal}
                  // to='/automation/process-automation/form-builder/create-new-form'
                  className='btn btn-primary fw-semibold px-4 py-2 rounded-pill shadow-sm text-decoration-none d-flex align-items-center'
                  style={{
                    background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e: any) => {
                    e.target.style.transform = 'translateY(-2px)'
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)'
                  }}
                  onMouseLeave={(e: any) => {
                    e.target.style.transform = 'translateY(0)'
                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <KTSVG
                    path='/media/icons/duotune/arrows/arr075.svg'
                    className='svg-icon-4 me-2'
                  />
                  Create New Form
                </button>
                <button
                  onClick={openUploadModal}
                  className='btn fw-semibold px-4 py-2 rounded-pill shadow-sm d-flex align-items-center'
                  style={{
                    border: '2px solid #0056b3',
                    transition: 'all 0.3s ease',
                    color: '#0056b3'
                  }}
                  onMouseEnter={(e: any) => {
                    e.target.style.transform = 'translateY(-2px)'
                    e.target.style.color = '#0056b3'
                    e.target.style.boxShadow = '0 4px 12px rgba(23, 162, 184, 0.3)'
                  }}
                  onMouseLeave={(e: any) => {
                    e.target.style.transform = 'translateY(0)'
                    e.target.style.color = '#0056b3'
                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <i className='bi bi-file-earmark-arrow-up-fill' style={{ color: '#0056b3' }}></i>
                  Upload Form
                </button>
                <button
                  onClick={() => setIsSignatureModalOpen(true)}
                  className='btn fw-semibold px-4 py-2 rounded-pill shadow-sm d-flex align-items-center'
                  style={{
                    border: `2px solid ${hasEmailSignature ? '#198754' : '#6c757d'}`,
                    transition: 'all 0.3s ease',
                    color: hasEmailSignature ? '#198754' : '#6c757d'
                  }}
                >
                  <i className={`bi ${hasEmailSignature ? 'bi-pen-fill' : 'bi-pen'} me-2`}></i>
                  {hasEmailSignature ? 'Update Signature' : 'Add Signature'}
                </button>
              </div>
            </div>
          </div>

          <button
            className='btn btn-outline-secondary'
            onClick={() => setShowTemplates(!showTemplates)}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#6c757d'
            }}
          >
            {showTemplates ? (
              'Hide templates ↑'
            ) : (
              <>
                <i className='bi bi-layout-wtf'></i>
                Explore templates
              </>
            )}
          </button>
        </div>

        {/* Explore Templates Section */}
        {showTemplates && (
          <div className='mb-5'>
            <h5 className='fw-bold text-dark my-8'>Explore templates</h5>
            <div className='row g-4 mb-4'>
              {templates.map((template, index) => (
                <div key={index} className='col-xl-3 col-lg-4 col-md-6 col-sm-6'>
                  <div
                    className='card border-0 h-100'
                    style={{
                      borderRadius: '12px',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      overflow: 'hidden' // This is crucial to clip the image's corners
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)'
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.12)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                    onClick={() => handleTemplateClick(template.name)}
                  >
                    {/* Header with solid color, matching the reference image */}
                    {/* <div
                      className='card-header border-0 text-white fw-bold d-flex align-items-center'
                      style={{
                        backgroundColor: template.headerColor,
                        padding: '12px 16px'
                      }}
                    >
                      {template.name}
                    </div> */}

                    {/* Image as the card body */}
                    <div className='card-body p-0'>
                      <img
                        src={template.imageUrl}
                        alt={`${template.name} template`}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block' // Removes any extra space below the image
                        }}
                      />
                      <div
                        className='position-absolute text-center top-0 start-0 w-100 p-3 mt-2'
                        style={{
                          borderRadius: '12px 12px 0 0'
                        }}
                      >
                        <h6 className='fw-bolder mb-0 text-white'>{template.name}</h6>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Forms Section */}
        <div className='card border-0' style={{ backgroundColor: 'white', borderRadius: '12px' }}>
          <div className='d-flex align-items-center gap-4 my-4 px-4'>
            {' '}
            {/* Added px-4 for padding on mobile */}
            <div className='d-flex align-items-center'>
              <button className='btn p-3' style={{ background: 'none', border: 'none' }}>
                {' '}
                {/* Reduced padding slightly */}
                <i className='bi bi-folder text-primary' style={{ fontSize: '18px' }}></i>
              </button>
              <span
                className='text-primary fw-semibold'
                style={{ borderBottom: '2px solid #007bff', paddingBottom: '4px' }}
              >
                My forms
              </span>
            </div>
          </div>
          <div className='card-body p-4'>
            {formBuilderData.length === 0 || formBuilderData.length === undefined ? (
              <div className='text-center py-10'>
                <div className='mb-4'>
                  <KTSVG
                    path='/media/icons/duotune/files/fil003.svg'
                    className='svg-icon-5x text-muted'
                  />
                </div>
                <h4 className='text-muted mb-3'>No forms available</h4>
                <p className='text-muted'>Start by creating your first form</p>
              </div>
            ) : (
              <div className='row g-4'>
                {formBuilderData
                  .map((formInfo: any, index: number) => (
                    // CHANGE 1: Added `col-12` to ensure cards stack on the smallest screens (mobile).
                    <div key={formInfo.id} className='col-12 col-md-6 col-lg-6 col-xl-3'>
                      <div
                        className='card shadow-sm border-0 position-relative overflow-hidden'
                        style={{
                          borderRadius: '20px',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}
                        onClick={() => openFormPreview(formInfo)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            openFormPreview(formInfo)
                          }
                        }}
                        role='button'
                        tabIndex={0}
                        aria-label={`Preview ${formInfo.name}`}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-5px)'
                          e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.15)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)'
                        }}
                      >
                        {/* Gradient Header */}
                        <div
                          className='card-header border-0 text-white position-relative'
                          style={{
                            background: getCardGradient(index),
                            height: '180px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <div className='text-center'>
                            <div className='d-flex align-items-center justify-content-center mb-2'>
                              {/* CHANGE 2: Made image responsive. It will now scale down on smaller cards. */}
                              <img
                                src='https://botgo.s3.ap-south-1.amazonaws.com/31327101750334166437-bg-change.png'
                                alt='Form checklist icon'
                                style={{
                                  width: '100%',
                                  maxWidth: '200px', // Prevents the image from becoming too large
                                  height: 'auto', // Maintains aspect ratio
                                  objectFit: 'contain'
                                }}
                              />
                            </div>
                          </div>

                          {/* Action Dropdown in top-right */}
                          {!formInfo.show && (
                            <div
                              className='position-absolute top-0 end-0 m-3'
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <FormBuilderAction
                                userActionDropdownMenuRefs={userActionDropdownMenuRefs}
                                formInfo={formInfo}
                                index={formInfo.formUuid}
                                setIsLoading={setIsLoading}
                                highlightMenu
                              />
                            </div>
                          )}
                        </div>

                        {/* Card Body */}
                        <div className='card-body d-flex flex-column form-builder-card__body'>
                          <div className='form-builder-card__title-row'>
                            <h5
                              className='form-builder-card__title text-truncate'
                              title={formInfo.name}
                            >
                              {formInfo.name.replace(/[_\-]/g, ' ').replace(/[^\w\s]/gi, '')}
                            </h5>
                            <span
                              className={`badge form-builder-card__type-badge ${getFormTypeBadgeClass(
                                formInfo
                              )}`}
                            >
                              {getFormTypeLabel(formInfo)}
                            </span>
                          </div>

                          <p className='form-builder-card__description'>
                            {formInfo.description?.trim() || 'No description available'}
                          </p>

                          <div
                            className='form-builder-card__footer'
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <Link
                              to={`/automation/table/${formInfo.formUuid}`}
                              className='form-builder-card__submissions-btn'
                              onClick={(e) => e.stopPropagation()}
                            >
                              <i className='bi bi-table' />
                              View Submissions
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                  .reverse()}
              </div>
            )}
          </div>
        </div>
      </div>
      <CreateFormModal
        show={isModalOpen}
        closeModal={closeModal}
        setIsModalOpen={setIsModalOpen}
        formJson={[]}
        templateId={0}
      />

      <FormCardPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={closePreviewModal}
        formInfo={previewFormInfo}
      />

      {/* Upload Form Modal */}
      <UploadFormModal show={isUploadModalOpen} handleClose={closeUploadModal} />

      <AddSignatureModal
        isOpen={isSignatureModalOpen}
        onClose={() => setIsSignatureModalOpen(false)}
        onSaved={() => refetchSignature()}
      />
    </div>
  )
}

export default FormBuilder
